import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { phone as phoneLib } from '@parsina/framework';

import { bffQuery } from '../lib/bffClient';
import { LOOKUP_PATIENTS_BY_PHONE } from '../lib/patient';

const NONE = Object.freeze([]);

/**
 * The records already on a mobile number, for every registration surface.
 *
 * Fed whatever the form holds — E.164 from `PhoneInput`, or a raw value a voice
 * transcript or a prefill wrote — and it only asks once that value PARSES. The kit
 * field emits `''` until the number is complete, so a lookup fires when the number
 * becomes valid, not on every keystroke; the short delay absorbs a paste followed
 * by a correction.
 *
 * Answers are kept per number for the life of the form: returning to a number the
 * desk already looked at asks nothing, and a number whose lookup failed is simply
 * asked again next time. A failed lookup is not a refusal — `addPatient` re-checks
 * the number and answers `PHONE_HAS_PATIENTS` itself.
 *
 * `seed` records candidates that arrived some other way (that refusal), so the
 * list the desk sees is the one the server just used.
 */
export function usePhoneCandidates(raw, { delay = 400 } = {}) {
  const number = raw ? (phoneLib.normalizePhone(String(raw).trim()) || '') : '';
  const cache = useRef(new Map());
  const [, rerender] = useReducer((n) => n + 1, 0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!number || cache.current.has(number)) { setLoading(false); return undefined; }
    let live = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await bffQuery(LOOKUP_PATIENTS_BY_PHONE, { phone: number });
        cache.current.set(number, data?.lookupPatientsByPhone?.candidates || NONE);
      } catch {
        // Not cached, so the next visit to this number asks again.
      } finally {
        if (live) { setLoading(false); rerender(); }
      }
    }, delay);
    return () => { live = false; clearTimeout(timer); };
  }, [number, delay]);

  const seed = useCallback((n, candidates) => {
    const key = phoneLib.normalizePhone(String(n || '').trim()) || '';
    if (!key) return;
    cache.current.set(key, candidates || NONE);
    rerender();
  }, []);

  return {
    number,
    candidates: (number && cache.current.get(number)) || NONE,
    loading: loading && !!number,
    seed,
  };
}

/**
 * The same, for every number of a list at once (PM-0536): the record editor and the
 * added rows of ثبت بیمار جدید, each of which is looked up as soon as it parses.
 *
 * `numbers` is the rows' values in order (any spelling; '' for a row not to look up).
 * `state(number)` answers `{ number, candidates, loading, failed }` for one of them;
 * `except` drops the record being edited from its own candidates. A failed lookup is
 * remembered only until the list changes, and is never a refusal: the save asks again.
 *
 * `seed(number, candidates)` takes the server's own answer (PHONE_HAS_PATIENTS), and
 * `markLinked(number, patientId)` turns a record the desk just added to this clinic
 * into a linked row, as a fresh lookup would.
 */
export function usePhoneListCandidates(numbers, { delay = 400, except = null } = {}) {
  const keys = (numbers || []).map((raw) => (raw ? (phoneLib.normalizePhone(String(raw).trim()) || '') : ''));
  const wanted = [...new Set(keys.filter(Boolean))].join('|');
  const cache = useRef(new Map());
  const inflight = useRef(new Set());
  const failed = useRef(new Set());
  const live = useRef(true);
  const [, rerender] = useReducer((n) => n + 1, 0);

  useEffect(() => () => { live.current = false; }, []);

  useEffect(() => {
    failed.current.clear();
    const todo = wanted ? wanted.split('|').filter((k) => !cache.current.has(k)) : [];
    if (!todo.length) return undefined;
    const timer = setTimeout(() => {
      for (const key of todo) {
        if (cache.current.has(key) || inflight.current.has(key)) continue;
        inflight.current.add(key);
        bffQuery(LOOKUP_PATIENTS_BY_PHONE, { phone: key })
          .then((data) => { cache.current.set(key, data?.lookupPatientsByPhone?.candidates || NONE); })
          .catch(() => { failed.current.add(key); })
          .finally(() => {
            inflight.current.delete(key);
            if (live.current) rerender();
          });
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [wanted, delay]);

  const state = useCallback((raw) => {
    const number = raw ? (phoneLib.normalizePhone(String(raw).trim()) || '') : '';
    const found = number ? cache.current.get(number) : undefined;
    const candidates = found ? found.filter((c) => c.patientId !== except) : NONE;
    return {
      number,
      candidates,
      loading: !!number && !found && !failed.current.has(number),
      failed: !!number && failed.current.has(number),
    };
  }, [except]);

  const seed = useCallback((n, candidates) => {
    const key = phoneLib.normalizePhone(String(n || '').trim()) || '';
    if (!key) return;
    cache.current.set(key, candidates || NONE);
    failed.current.delete(key);
    rerender();
  }, []);

  const markLinked = useCallback((n, patientId) => {
    const key = phoneLib.normalizePhone(String(n || '').trim()) || '';
    const list = cache.current.get(key);
    if (!list) return;
    cache.current.set(key, list.map((c) => (c.patientId === patientId ? { ...c, isLinkedToThisClinic: true } : c)));
    rerender();
  }, []);

  return { state, seed, markLinked };
}

export default usePhoneCandidates;
